// src/pages/user/EnhancedUserDashboard.tsx
import React, { useState, useEffect, useRef, memo } from "react"
import { useNavigate } from "react-router-dom"
import UserDashboardLayout from "../../components/layout/UserDashboardLayout"
import {
  BookOpen,
  Clock,
  CheckCircle,
  PlayCircle,
  Target,
  ShoppingCart,
  ChevronRight,
  Video,
} from "lucide-react"
import { getAllCourses, getAllCourseModules, getLessonsByModuleId } from "../../api/courseService"
import { getPurchaseData, getCourseProgress } from "../../api/packageService"
import { formatSeconds } from '../../utils/time'
import "../../style/enhanced-user-dashboard.css";
import Hls from 'hls.js'

interface Course {
  _id: string
  title: string
  description: string
  thumbnail: string
  createdAt: string
  isUnlocked: boolean
  status: "yet_to_start" | "in_progress" | "completed"
  duration?: string
  language: string
  desktopAvailable: boolean
  moduleCount?: number
  lessonCount?: number
  progress?: number
  isPurchased?: boolean
  courseType?: string
  type: 'course';
  PublishStatus?: string;
  daysLeft?: number; // Add daysLeft property
  expiry?: string; // Add expiry property for dynamic calculation
}

const EnhancedUserDashboard: React.FC = () => {
  const [userName, setUserName] = useState<string>("Student")
  const [purchasedCourses, setPurchasedCourses] = useState<Course[]>([])
  const [recommendedCourses, setRecommendedCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [, setUserId] = useState<string>("")
  const navigate = useNavigate();
  
  // Dedicated Video Component with optimized rendering
  const OptimizedVideoThumbnail = memo(({ src, alt }: { src: string; alt: string }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    
    useEffect(() => {
      if (!videoRef.current) return;
      
      // Reset states
      setIsLoading(true);
      setHasError(false);
      
      // Clean up previous HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      
      const videoElement = videoRef.current;
      
      // Handle video events
      const handleLoadedData = () => {
        setIsLoading(false);
      };
      
      const handleError = (e: any) => {
        console.error("Video error for:", src, e);
        setIsLoading(false);
        setHasError(true);
        // Fallback to placeholder
        if (videoElement) {
          videoElement.src = "/placeholder.svg";
        }
      };
      
      videoElement.addEventListener('loadeddata', handleLoadedData);
      videoElement.addEventListener('error', handleError);
      
      // Check if this is a streaming URL that needs HLS
      if (src.includes('/stream/') || src.includes('.m3u8')) {
        if (Hls.isSupported()) {
          // Initialize HLS player
          const hls = new Hls({
            enableWorker: true,
          });
          
          hls.loadSource(src);
          hls.attachMedia(videoElement);
          
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            console.log("HLS manifest parsed successfully for:", src);
            setIsLoading(false);
          });
          
          hls.on(Hls.Events.ERROR, (event, data) => {
            console.error("HLS error for:", src, data);
            if (data.fatal) {
              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  hls.startLoad();
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  hls.recoverMediaError();
                  break;
                default:
                  hls.destroy();
                  setHasError(true);
                  setIsLoading(false);
                  break;
              }
            }
          });
          
          hlsRef.current = hls;
        } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
          // For Safari
          videoElement.src = src;
          // Try to play after setting src
          videoElement.play().catch((e: any) => {
            console.log("Autoplay prevented:", e);
          });
        } else {
          // Fallback for browsers that don't support HLS
          videoElement.src = src;
        }
      } else {
        // For regular video files
        videoElement.src = src;
      }
      
      // Cleanup
      return () => {
        videoElement.removeEventListener('loadeddata', handleLoadedData);
        videoElement.removeEventListener('error', handleError);
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      };
    }, [src]);
    
    return (
      <div className="relative w-full h-full">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}
        {hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
            <div className="text-white text-sm">Failed to load video</div>
          </div>
        )}
        <video
          ref={videoRef}
          className="w-full h-full object-cover transition-opacity duration-300"
          style={{ backgroundColor: 'black', opacity: isLoading || hasError ? 0 : 1 }}
          onError={(e) => {
            console.error("Video element error:", e);
            setHasError(true);
            setIsLoading(false);
          }}
        />
      </div>
    );
  });

  // Dedicated Image Component with optimized rendering
  const OptimizedImageThumbnail = memo(({ src, alt }: { src: string; alt: string }) => {
    return (
      <img
        src={src}
        alt={alt}
        className="course-thumbnail"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.src = "/placeholder.svg";
        }}
      />
    );
  });
  
  // Helper function to determine if a URL is a video
  const isVideoUrl = (url: string): boolean => {
    // Check for data URLs - only video data URLs
    if (url.startsWith('data:video')) {
      return true;
    }
    
    // Check for streaming URLs
    if (url.includes('/stream/')) {
      return true;
    }
    
    // Check for file extensions in the URL
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    return videoExtensions.some(ext => url.includes(ext));
  };
  
  // Helper function to get course thumbnail with proper token handling
  const getCourseThumbnail = (thumbnailUrl: string): string => {
    if (!thumbnailUrl) return "/placeholder.svg";
    
    // Check if it's already a data URL
    if (thumbnailUrl.startsWith('data:')) {
      return thumbnailUrl;
    }
    
    // Check if token is already present in the URL
    if (thumbnailUrl.includes('token=')) {
      return thumbnailUrl;
    }
    
    // Check if it's a streaming URL
    if (thumbnailUrl.includes('/stream/')) {
      // For streaming URLs, we might need to append the token differently
      const authToken = localStorage.getItem("AUTH_TOKEN");
      if (authToken) {
        // For streaming URLs, the token is usually part of the URL path, not a query parameter
        // But we'll still append it as a query parameter for consistency
        const separator = thumbnailUrl.includes('?') ? '&' : '?';
        const urlWithToken = `${thumbnailUrl}${separator}token=${encodeURIComponent(authToken)}`;
        return urlWithToken;
      }
      return thumbnailUrl;
    }
    
    const authToken = localStorage.getItem("AUTH_TOKEN");
    if (authToken) {
      const separator = thumbnailUrl.includes('?') ? '&' : '?';
      const urlWithToken = `${thumbnailUrl}${separator}token=${encodeURIComponent(authToken)}`;
      return urlWithToken;
    }
    return thumbnailUrl;
  };
  
  // Course Card Component
  const CourseCard: React.FC<{ course: Course; onClick: (e: React.MouseEvent) => void }> = ({ course, onClick }) => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'completed': return 'bg-green-100 text-green-800';
        case 'in_progress': return 'bg-blue-100 text-blue-800';
        case 'yet_to_start': return 'bg-gray-100 text-gray-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    };

    // Calculate days left dynamically from expiry date
    const calculateDaysLeft = () => {
      if (course.expiry) {
        const expiryDate = new Date(course.expiry);
        const currentDate = new Date();
        const timeDiff = expiryDate.getTime() - currentDate.getTime();
        const daysLeft = Math.ceil(timeDiff / (1000 * 3600 * 24));
        return daysLeft;
      }
      return course.daysLeft; // Fallback to API provided daysLeft if expiry not available
    };

    const daysLeft = calculateDaysLeft();
    const isExpired = daysLeft !== undefined && daysLeft <= 0;

    return (
      <div 
        className={`course-card ${course.status === 'completed' ? 'completed' : ''} ${isExpired ? 'opacity-50 pointer-events-none' : ''}`}
        onClick={!isExpired ? onClick : undefined}
      >
        <div className="relative">
          {course.thumbnail ? (
            (() => {
              const thumbnailUrl = getCourseThumbnail(course.thumbnail);
              const isVideo = isVideoUrl(thumbnailUrl);
              
              if (isVideo) {
                return (
                  <OptimizedVideoThumbnail 
                    src={thumbnailUrl} 
                    alt={course.title} 
                  />
                );
              } else {
                return (
                  <OptimizedImageThumbnail 
                    src={thumbnailUrl} 
                    alt={course.title} 
                  />
                );
              }
            })()
          ) : (
            <div className="w-full h-32 bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
              <BookOpen className="h-8 w-8 text-blue-500" />
            </div>
          )}
          <div className="absolute top-2 right-2">
            <span className={`status-badge ${getStatusColor(course.status)}`}>
              {course.status === 'completed' ? 'Completed' : course.status === 'in_progress' ? 'In Progress' : 'Yet to Start'}
            </span>
          </div>
          {course.courseType === 'Webinar' && (
            <div className="absolute top-2 left-2">
              <span className="status-badge" style={{ color: '#c2410c', backgroundColor: 'rgba(194, 65, 12, 0.1)' }}>
                <Video size={12} style={{ display: 'inline', marginRight: 3, verticalAlign: 'text-bottom' }} />
                Webinar
              </span>
            </div>
          )}
          {/* Expired overlay */}
          {isExpired && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-t-lg">
              <span className="text-white font-bold text-sm bg-red-600 px-2 py-1 rounded">
                EXPIRED
              </span>
            </div>
          )}
        </div>
        <div className="course-content">
          <h3 className="course-title">{course.title}</h3>
          <div className="course-meta">
            <span>{course.lessonCount || 0} lessons</span>
            {course.duration && course.duration !== 'N/A' && <span>{course.duration}</span>}
          </div>
          {/* Display days left if course is in progress and has 10 or fewer days left */
          }
          {course.status === 'in_progress' && daysLeft !== undefined && daysLeft <= 10 && daysLeft >= 0 && (
            <div className="mt-1 text-xs text-orange-600 font-medium flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              <span>{daysLeft} {daysLeft === 1 ? 'day' : 'days'} left</span>
            </div>
          )}
          {/* Progress bar moved to bottom of course content */}
          {course.progress !== undefined && course.progress > 0 && (
            <div className="mt-2">
              <div className="flex items-center">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${course.progress}%` }}
                  ></div>
                </div>
                <span className="ml-2 text-xs font-medium text-gray-600">
                  {Math.round(course.progress)}%
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    // Get user name and ID from localStorage
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      try {
        const userData = JSON.parse(storedUserData);
        if (userData.name) {
          setUserName(userData.name);
        }
        if (userData._id) {
          setUserId(userData._id);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        const storedName = localStorage.getItem('userName') || localStorage.getItem('name') || localStorage.getItem('user_name');
        if (storedName) {
          setUserName(storedName);
        }
      }
    } else {
      const storedName = localStorage.getItem('userName') || localStorage.getItem('name') || localStorage.getItem('user_name');
      if (storedName) {
        setUserName(storedName);
      }
    }
  }, [])

  // Fetch user's purchased courses and recommended courses with real progress data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Get user ID from localStorage
        const storedUserData = localStorage.getItem('userData');
        if (!storedUserData) {
          setLoading(false);
          return;
        }
        
        let userId: string = "";
        let userRole: string = "";
        try {
          const userData = JSON.parse(storedUserData);
          userId = userData._id;
          userRole = userData.role || "";
          setUserId(userId);
        } catch (error) {
          console.error('Error parsing user data:', error);
          setLoading(false);
          return;
        }
        
        if (!userId) {
          setLoading(false);
          return;
        }
        
        // Fetch purchase data
        const purchaseResponse = await getPurchaseData(userId);
        const purchasedCourseMap = new Map<string, any>();
        
        if (purchaseResponse.status === 200 && purchaseResponse.data) {
          // Extract purchased course data with progress
          purchaseResponse.data.purchases.forEach((purchase: any) => {
            purchase.courses.forEach((course: any) => {
              purchasedCourseMap.set(course.courseId, {
                courseViewPercent: course.courseViewPercent,
                certificateSent: course.certificateSent
              });
            });
          });
        }
        
        // For each purchased course, fetch detailed progress data
        const detailedProgressMap = new Map<string, any>();
        const promises: Promise<void>[] = [];
        
        purchasedCourseMap.forEach((courseData, courseId) => {
          promises.push(
            getCourseProgress(courseId, userId)
              .then((progressResponse: any) => {
                if (progressResponse.status === 200 && progressResponse.data) {
                  detailedProgressMap.set(courseId, progressResponse.data.getCourseProgress);
                }
              })
              .catch((error: any) => {
                console.error(`Error fetching progress for course ${courseId}:`, error);
              })
          );
        });
        
        // Wait for all progress data to be fetched
        await Promise.all(promises);
        
        // Fetch all courses
        const coursesResponse = await getAllCourses();
        
        if (coursesResponse.status === 200 && coursesResponse.data) {
          // Process courses to determine which are purchased and their status
          const allCourses: Course[] = await Promise.all(
            coursesResponse.data
              .filter((course: any) => course.PublishStatus === "Published")
              .map(async (course: any) => {
                const isInPurchaseMap = purchasedCourseMap.has(course._id);
                const isPrivilegedUser = userRole === 'admin' || userRole === 'moderator' || userRole === 'support_user';
                const hasAccess = isPrivilegedUser || isInPurchaseMap || course.courseType === 'Webinar';
                let progress = 0;
                let status: "yet_to_start" | "in_progress" | "completed" = "yet_to_start";
                let duration = undefined;

                // For accessible courses/webinars, get detailed progress data
                if (hasAccess) {
                  const detailedProgress = detailedProgressMap.get(course._id);
                  if (detailedProgress) {
                    // Use the total progress percent from detailed data
                    progress = detailedProgress.totalProgressPercent || 0;

                    // Calculate average watch time as fallback
                    if (detailedProgress.lessonProgress && detailedProgress.lessonProgress.length > 0) {
                      const totalWatchTime = detailedProgress.lessonProgress.reduce((sum: number, lesson: any) =>
                        sum + (lesson.watchTime || 0), 0);

                      // If we have watch time data, use it to determine if course has been started
                      if (totalWatchTime > 0) {
                        // Override progress with a value that indicates the course has been started
                        if (progress <= 0) {
                          progress = 1; // Set to minimum non-zero value to indicate started
                        }
                      }
                    }
                  } else if (isInPurchaseMap) {
                    // Fallback to purchase data
                    const progressData = purchasedCourseMap.get(course._id);
                    progress = progressData.courseViewPercent || 0;
                  }

                // Ensure progress is a valid number
                if (typeof progress !== 'number' || isNaN(progress)) {
                  progress = 0;
                }
                
                // Determine status based on progress
                if (progress <= 0) {
                  status = "yet_to_start";
                } else if (progress >= 95) {  // Changed from >= 100 to >= 99
                  status = "completed";
                } else {
                  status = "in_progress";
                }
              }
              
              // Get expiry and daysLeft from detailed progress data
              let expiry: string | undefined;
              let daysLeft: number | undefined;
              const detailedProgress = detailedProgressMap.get(course._id);
              if (detailedProgress) {
                expiry = detailedProgress.expiry;
                daysLeft = detailedProgress.daysLeft ?? undefined;
              }
              
// Calculate duration and lesson count for all courses
let lessonCount = 0;
let moduleCount = 0;

try {
  const modulesResponse = await getAllCourseModules();

  if (modulesResponse.status === 200 && modulesResponse.data) {
    // Get modules belonging to this course
    const courseModules = modulesResponse.data.filter(
      (module: any) =>
        module.courseId &&
        module.courseId._id === course._id
    );

    // Count modules
    moduleCount = courseModules.length;

    // Calculate total duration + total lessons
    let totalDuration = 0;

    for (const module of courseModules) {
      const lessonsResponse = await getLessonsByModuleId(module._id);

      if (
        lessonsResponse.status === 200 &&
        Array.isArray(lessonsResponse.data)
      ) {
        // Count lessons
        lessonCount += lessonsResponse.data.length;

        // Calculate duration
        lessonsResponse.data.forEach((lesson: any) => {
          totalDuration += Number(lesson.duration) || 0;
        });
      }
    }

    if (totalDuration > 0) {
      duration = formatSeconds(totalDuration);
    }
  }
} catch (error) {
  console.error(
    `Error calculating duration/lessons for course ${course._id}:`,
    error
  );
}              
              
              return {
                _id: course._id,
                title: course.title,
                description: course.description,
                thumbnail: course.thumbnail || "",
                createdAt: course.createdAt,
                isUnlocked: hasAccess,
                status,
                duration,
                language: course.language,
                desktopAvailable: course.desktopAvailable,
                moduleCount,
                lessonCount,
                progress: hasAccess ? progress : undefined,
                isPurchased: hasAccess,
                courseType: course.courseType || 'Course',
                type: 'course' as const,
                PublishStatus: course.PublishStatus,
                expiry,
                daysLeft,
              };
            })
        );
        
        // Separate purchased and recommended courses
        const purchased = allCourses.filter(course => course.isPurchased);
        const recommended = allCourses.filter(course => !course.isPurchased);
        
        setPurchasedCourses(purchased);
        setRecommendedCourses(recommended);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  fetchDashboardData();
}, []);
  
  // Show all purchased courses (no filtering)
  const filteredPurchasedCourses = purchasedCourses;

  // Calculate statistics
  const totalEnrolled = purchasedCourses.length;
  const completedCourses = purchasedCourses.filter(c => c.status === 'completed').length;
  const inProgressCourses = purchasedCourses.filter(c => c.status === 'in_progress').length;
  // const averageProgress = purchasedCourses.length > 0 
    // ? Math.round(purchasedCourses.reduce((sum, course) => sum + (course.progress || 0), 0) / purchasedCourses.length)
    // : 0;
    
  // Calculate average hours from course durations
  const averageHours = purchasedCourses.length > 0 
    ? Math.round(purchasedCourses.reduce((sum, course) => {
        // Extract numeric value from duration string (e.g., "2 hours" -> 2)
        let hours = 0;
        if (course.duration && typeof course.duration === 'string') {
          const match = course.duration.match(/(\d+)/);
          if (match) {
            hours = parseInt(match[1], 10) || 0;
          }
        }
        return sum + hours;
      }, 0) / purchasedCourses.length)
    : 0;

  return (
    <UserDashboardLayout>
      <div className="enhanced-user-dashboard">
        {/* Welcome Section - Made more compact */}
        <div className="welcome-section" onClick={() => window.location.hash = "/user-profile"}>
          <div className="welcome-content">
            <h1 className="welcome-title">
              Welcome back, <span className="highlight">{userName}</span>! 👋
            </h1>
            <p className="welcome-subtitle">
              Continue your learning journey and explore new courses to enhance your skills.
            </p>
          </div>
        </div>
        
        {/* Dashboard Stats - Simplified */}
        <div className="dashboard-stats">
          <div className="stat-card" onClick={() => navigate("/user-offerings")}>
            <div className="stat-icon bg-blue-100 text-blue-600">
              <BookOpen size={24} />
            </div>
            <div className="stat-info">
              <p className="stat-value">{totalEnrolled}</p>
              <p className="stat-label">Courses Enrolled</p>
            </div>
          </div>
          
          <div className="stat-card" onClick={() => navigate("/UserPurchases")}>
            <div className="stat-icon bg-green-100 text-green-600">
              <CheckCircle size={24} />
            </div>
            <div className="stat-info">
              <p className="stat-value">{completedCourses}</p>
              <p className="stat-label">Courses Completed</p>
            </div>
          </div>
          
          <div className="stat-card" onClick={() => navigate("/UserPurchases?filter=in_progress")}>
            <div className="stat-icon bg-purple-100 text-purple-600">
              <PlayCircle size={24} />
            </div>
            <div className="stat-info">
              <p className="stat-value">{inProgressCourses}</p>
              <p className="stat-label">In Progress</p>
            </div>
          </div>
          
          <div className="stat-card" onClick={() => navigate("/UserPurchases")}>
            <div className="stat-icon bg-orange-100 text-orange-600">
              <Target size={24} />
            </div>
            <div className="stat-info">
              <p className="stat-value">{averageHours}</p>
              <p className="stat-label">Average Hours</p>
            </div>
          </div>
        </div>
        
        {/* Loading State */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading your dashboard...</p>
          </div>
        ) : (
          <>
            {/* My Courses Section */}
            {filteredPurchasedCourses.length > 0 ? (
              <div className="section" onClick={() => navigate("/UserPurchases")}>
                <div className="section-header">
                  <div>
                    <h2 className="section-title">My Courses</h2>
                    <p className="section-subtitle">Continue learning from where you left off</p>
                  </div>
                  <div className="flex items-center text-blue-600 font-medium text-sm">
                    <span>View All</span>
                    <ChevronRight size={16} className="ml-1" />
                  </div>
                </div>
                
                <div className="courses-grid">
                  {filteredPurchasedCourses.slice(0, 4).map((course) => (
                    <CourseCard 
                      key={course._id} 
                      course={course} 
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        navigate(`/video/${course._id}`);
                      }} 
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <ShoppingCart className="empty-icon" size={48} />
                <h3 className="empty-title">No Courses Purchased Yet</h3>
                <p className="empty-description">
                  You haven't purchased any courses yet. Browse our offerings to get started on your learning journey!
                </p>
                <button 
                  className="browse-button"
                  onClick={() => navigate("/user-offerings")}
                >
                  Browse Courses
                </button>
              </div>
            )}
            
            {/* Recommended Courses Section - Only show if we have them */}
            {recommendedCourses.length > 0 && (
              <div className="section" onClick={() => navigate("/user-offerings")}>
                <div className="section-header">
                  <div>
                    <h2 className="section-title">Recommended For You</h2>
                    <p className="section-subtitle">Explore new courses to enhance your skills</p>
                  </div>
                  <div className="flex items-center text-blue-600 font-medium text-sm">
                    <span>View All</span>
                    <ChevronRight size={16} className="ml-1" />
                  </div>
                </div>
                
                <div className="courses-grid">
                  {recommendedCourses.slice(0, 4).map((course) => (
                    <CourseCard 
                      key={course._id} 
                      course={course} 
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        navigate(`/user-offerings`);
                      }} 
                    />
                  ))}VideoPlayer
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </UserDashboardLayout>
  )
}

export default EnhancedUserDashboard